import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import AssistantAttachmentSchema, {
  AssistantAttachmentJSON,
  AssistantAttachmentParams,
} from "ai-assistant/attachments/assistant-attachment-schema";
import { AssistantAttachmentTextState } from "ai-assistant/attachments/assistant-attachment-text-state";
import { DocumentFileType } from "documents/document-file-type";

const typeLabels: Record<string, string> = {
  [DocumentFileType.PDF]: "PDF",
  [DocumentFileType.DOC]: "DOC",
  [DocumentFileType.DOCX]: "DOCX",
  [DocumentFileType.ODT]: "ODT",
};

/**
 * A file the user attached to an assistant message (#6504).
 *
 * Uploaded on its own before the message is sent, and named by the message through its id. It is
 * the user's alone: nobody else can open it, and nothing files it anywhere unless the user confirms
 * a prepared action that does.
 */
export default class AssistantAttachment {
  public static create(values: AssistantAttachmentParams): AssistantAttachment {
    const parsed = AssistantAttachmentSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid AssistantAttachment parameters.");
    }
    return new AssistantAttachment(parsed);
  }

  public static fromJSON(json: AssistantAttachmentJSON): AssistantAttachment {
    if (isNullOrUndefined(json)) return json;
    return new AssistantAttachment(AssistantAttachmentSchema.deserialize(json));
  }

  public readonly id: Guid;
  public readonly fileName: string;
  public readonly mediaType: string;
  public readonly sizeBytes: number;
  public readonly uploadedAt: AHDate;
  public readonly textState: AssistantAttachmentTextState;

  /** A short name for the file's type, such as "PDF", for a label beside its name. */
  public get typeLabel(): string {
    return typeLabels[this.mediaType.toLowerCase()] ?? "File";
  }

  public constructor(params: AssistantAttachmentParams) {
    this.id = params.id;
    this.fileName = params.fileName;
    this.mediaType = params.mediaType;
    this.sizeBytes = params.sizeBytes;
    this.uploadedAt = params.uploadedAt;
    this.textState = params.textState;
  }

  public toJSON(): AssistantAttachmentJSON {
    return AssistantAttachmentSchema.serialize(this);
  }

  public isEqualTo(other: AssistantAttachment): boolean {
    return this.id.isEqualTo(other.id);
  }

  public toString(): string {
    return `${this.fileName} (${this.typeLabel})`;
  }
}
